<script setup>
import {computed, ref, watch} from 'vue'
import {usePageTemplatesContentAreasStore} from '@/stores/pageTemplatesContentAreas'
import {usePageSectionsStore} from '@/stores/pageSections'
import {useItemDataStore} from '@/stores/itemData'
import {useSiteTreeStore} from '@/stores/siteTree'

const props = defineProps({
    siteTreeId: String,
    pageId: Number
})

const pageTemplatesContentAreasStore = usePageTemplatesContentAreasStore()
const pageSectionsStore = usePageSectionsStore()
const itemDataStore = useItemDataStore()
const siteTreeStore = useSiteTreeStore(props.siteTreeId)

// Initialize page sections
pageSectionsStore.init()

const page = ref(null)

// Watch props.pageId and fetch page data
watch(() => props.pageId, async (newPageId) => {
    if (newPageId) {
        page.value = await itemDataStore.get({
            tableName: 'pages',
            id: newPageId
        })
    } else {
        page.value = null
    }
}, {immediate: true})

const pageTemplatesContentAreas = computed(() => {
    if (page.value) {
        return pageTemplatesContentAreasStore.pageTemplatesContentAreas?.[page.value.templateId]
    }
    return undefined
})

const selectedContentArea = computed(() => {
    return siteTreeStore.propagationData?.find(o => o.pageId === props.pageId)?.contentArea
})

const sections = computed(() => {
    return (pageSectionsStore.items || []).filter(o => o.pageId === props.pageId)
})

watch(sections, (newSections) => {
    if (newSections?.length) {
        const firstAreaValue = newSections[0]?.areas?.[0]?.value
        if (firstAreaValue !== undefined) {
            siteTreeStore.setPropagationData({
                pageId: props.pageId,
                contentArea: firstAreaValue
            })
        }
    }
}, {immediate: true, deep: true})

const setPropagatedContent = (e) => {
    const contentArea = e.target.value
    siteTreeStore.setPropagationData({
        pageId: props.pageId,
        contentArea
    })
}
</script>

<template>
    <select @change="setPropagatedContent" @click.stop.prevent class="form-select form-select-sm w-auto">
        <!-- By default, the first content area is assigned to a page when it's selected. This avoids the need to
             validate the menus, which would add a lot of complexity to the propagation feature. -->
        <option selected disabled>Select&hellip;</option>

        <template v-if="sections?.length">
            <optgroup v-for="(section, k) in sections" :key="k" :label="'Section ' + (k + 1)">
                <option v-for="area in section.areas" :key="area.value" :value="area.value"
                        :selected="area.value === selectedContentArea">
                    {{ area.text }}
                </option>
            </optgroup>
        </template>

        <template v-else>
            <option v-for="contentArea in pageTemplatesContentAreas" :key="contentArea" :value="contentArea"
                    :selected="contentArea === selectedContentArea">
                {{ contentArea }}
            </option>
        </template>
    </select>
</template>